'use client';

import { useState } from 'react';
import { pascalCase } from 'change-case';
import { toast } from '~/utils/toast';
import TaskManagerModal, {
  OAuthSteps,
} from '~/app/(secured)/[workspaceId]/settings/task-manager/_components/TaskManagerModal';
import { ConnectIconTile } from '~/app/(secured)/[workspaceId]/settings/task-manager/_components/ConnectIconTile';
import { useModal } from '~/components/Modal';
import { api, skipBatchOption } from '~/utils/api';
import { getProviderLogo } from '~/utils/logo';
import { cn } from '~/lib/utils';

import { RemoteTaskManagerProvider, type TaskManagerType } from '@prisma/client';

const DEFAULT_PROVIDERS: RemoteTaskManagerProvider[] = [
  RemoteTaskManagerProvider.ASANA,
  RemoteTaskManagerProvider.JIRA,
  RemoteTaskManagerProvider.SMARTSHEET,
  RemoteTaskManagerProvider.WRIKE,
];

type ConnectTaskManagerProps = {
  skipProjectSelection?: boolean;
  onProviderClickHandler?: () => void;
  providers?: RemoteTaskManagerProvider[];
  taskManagerType: TaskManagerType;
};

export default function ConnectTaskManager({
  skipProjectSelection,
  onProviderClickHandler,
  providers,
  taskManagerType,
}: ConnectTaskManagerProps) {
  const { isLoading: providerConnectedStatusLoading, data: getProviderConnectedStatus } =
    api.oAuth.getProviderConnectedStatus.useQuery(undefined, skipBatchOption);

  const [remoteTaskManagerProviderForOAuth, setRemoteTaskManagerProviderForOAuth] =
    useState<RemoteTaskManagerProvider | null>(null);

  const utils = api.useUtils();

  const {
    modalRef: oAuthModalRef,
    openModal: openOAuthModal,
    closeModal: closeOAuthModal,
  } = useModal();

  const visibleProviders = providers ?? DEFAULT_PROVIDERS;

  const onClickProvider = (provider: RemoteTaskManagerProvider) => {
    setRemoteTaskManagerProviderForOAuth(provider);
    if (providerConnectedStatusLoading) {
      toast.info('Please wait while we check your connection status');
      return;
    }
    openOAuthModal();
    onProviderClickHandler?.();
  };

  const getInitialStep = () => {
    if (providerConnectedStatusLoading || !remoteTaskManagerProviderForOAuth) {
      return OAuthSteps.ALLOW_ACCESS;
    }
    const provider = getProviderConnectedStatus![remoteTaskManagerProviderForOAuth];
    if (provider?.connected) {
      return OAuthSteps.PROJECT_SELECTION;
    }
    return OAuthSteps.ALLOW_ACCESS;
  };

  return (
    <>
      <ul className={cn('grid gap-2.5', visibleProviders.length > 1 && 'sm:grid-cols-2')}>
        {visibleProviders.map((provider) => {
          const name = pascalCase(provider);

          return (
            <li key={provider}>
              <button
                type="button"
                disabled={providerConnectedStatusLoading}
                aria-label={`Connect ${name}`}
                className="bg-card border-border/80 hover:border-foreground/12 hover:bg-accent/50 focus-visible:ring-ring active:bg-accent/70 flex w-full items-center gap-2.5 rounded-xl border p-3 text-left transition-[border-color,background-color] duration-200 ease-out focus-visible:ring-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
                onClick={() => onClickProvider(provider)}
              >
                <ConnectIconTile src={getProviderLogo(provider).src} alt="" size="sm" />
                <span className="text-foreground min-w-0 truncate text-[13px] leading-4 font-medium tracking-tight">
                  {name}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <TaskManagerModal
        modalRef={oAuthModalRef}
        closeModal={async () => {
          await utils.oAuth.getProviderConnectedStatus.refetch();
          closeOAuthModal();
        }}
        taskManagerType={taskManagerType}
        initialStep={getInitialStep()}
        provider={remoteTaskManagerProviderForOAuth}
        skipProjectSelection={skipProjectSelection}
      />
    </>
  );
}
